/* homeData.js — shared fetch/format helpers for the Research home rebuild.
 * Only reads existing public endpoints. Never invents numbers.
 */
import { researchNavigate } from "../desk/researchNavigation";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/research/editions`;

async function boundedFetch(url, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try { return await fetch(url, {...options, signal: controller.signal}); }
  finally { clearTimeout(timer); }
}

export async function fetchEditionCatalog() {
  const response = await boundedFetch(`${API}/catalog?limit=6`, {cache: 'no-store', headers: apiHeaders()});
  if (!response.ok) throw new Error('Edition archive unavailable');
  const data = await response.json();
  return Array.isArray(data.editions) ? data.editions : [];
}

export const apiHeaders = () => ({ "X-API-Key": process.env.REACT_APP_API_KEY || "" });

export function go(path) {
  return (event) => {
    if (event && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    if (event) event.preventDefault();
    researchNavigate(path);
  };
}

export async function fetchLatestEdition() {
  const response = await boundedFetch(`${API}/latest`, { cache: "no-store", headers: apiHeaders() });
  if (!response.ok) throw new Error(`latest edition unavailable (${response.status})`);
  return response.json();
}

export async function fetchEditionByDate(date) {
  const response = await boundedFetch(`${API}/${date}`, { cache: "no-store", headers: apiHeaders() });
  if (!response.ok) throw new Error(`edition ${date} unavailable (${response.status})`);
  return response.json();
}

export async function fetchFeed(limit = 30) {
  const response = await boundedFetch(`${API}/feed`, { cache: "no-store", headers: apiHeaders() });
  if (!response.ok) throw new Error(`headline feed unavailable (${response.status})`);
  const data = await response.json();
  const items = Array.isArray(data.items) ? data.items : [];
  return items.slice(0, limit);
}

export function editionUrl(edition) {
  if (!edition) return "/research/archive";
  const id = edition.editionId || "";
  const match = id.match(/^(\d{4}-\d{2}-\d{2}):(morning|evening|event|legacy):r([1-9]\d*)$/);
  if (match) return `/research/editions/${match[1]}/${match[2]}/r${match[3]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(id)) return `/research/editions/${id}`;
  return edition.date ? `/research/editions/${edition.date}` : "/research/archive";
}

export function storyUrl(edition) {
  const date = edition?.date || (edition?.editionId || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `/research/story/${date}` : "/research/archive";
}

export function formatMoney(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return "$" + Number(value).toLocaleString("en-US", { maximumFractionDigits: Number(value) < 10 ? 2 : 0 });
}

export function formatPct(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const v = Number(value);
  return `${v > 0 ? "▲" : v < 0 ? "▼" : "•"} ${Math.abs(v).toFixed(2)}%`;
}

export function chgClass(value) {
  if (value == null || !Number.isFinite(Number(value))) return "rh-flat";
  return Number(value) > 0 ? "rh-up" : Number(value) < 0 ? "rh-down" : "rh-flat";
}

export function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function chicagoDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short", month: "short", day: "numeric",
    timeZone: "America/Chicago",
  }).format(d);
}


/* ---------- Research home data APIs (/api/research/home/*) ---------- */
const HOME_API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/research/home`;

async function homeGet(path) {
  const response = await boundedFetch(`${HOME_API}${path}`, { cache: "no-store", headers: apiHeaders() });
  if (!response.ok) throw new Error(`${path} unavailable (${response.status})`);
  return response.json();
}

export function fetchHomeCoins() {
  return homeGet("/coins").then((d) => ({ coins: d.coins || [], global: d.global || null, as_of: d.as_of || null }));
}

export function fetchHomeChart(symbol, days = 30) {
  return homeGet(`/charts/${encodeURIComponent(symbol)}?days=${days}`)
    .then((d) => ({ symbol: d.symbol, points: d.points || [], source: d.source || null }));
}

export function fetchHomeCalendar() {
  return homeGet("/calendar").then((d) => d.events || []);
}

export function fetchHomeStance() {
  return homeGet("/stance");
}

export function fetchHomeSectors() {
  return homeGet("/sectors").then((d) => ({ sectors: d.sectors || [], as_of: d.as_of || null }));
}

/* Compact money: $1.24B, $860M, $12.4K */
export function formatBig(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const v = Number(value);
  const abs = Math.abs(v);
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

/* Static coin directory — names/symbols only, no market data. */
export const COINS = {
  BTC: { name: "Bitcoin", sector: "Layer 1" },
  ETH: { name: "Ethereum", sector: "Layer 1" },
  SOL: { name: "Solana", sector: "Layer 1" },
  XRP: { name: "XRP", sector: "Layer 1" },
  SUI: { name: "Sui", sector: "Layer 1" },
  NEAR: { name: "NEAR Protocol", sector: "Layer 1" },
  AVAX: { name: "Avalanche", sector: "Layer 1" },
  LINK: { name: "Chainlink", sector: "DeFi" },
  UNI: { name: "Uniswap", sector: "DeFi" },
  BNB: { name: "BNB", sector: "Exchange" },
  DOGE: { name: "Dogecoin", sector: "Memes" },
  ADA: { name: "Cardano", sector: "Layer 1" },
};

export const SECTORS = [
  { name: "Layer 1", coins: "BTC · ETH · SOL", big: true },
  { name: "DeFi", coins: "top 3", big: true },
  { name: "AI", coins: "top 3", big: true },
  { name: "Exchange", coins: "BNB · top 2" },
  { name: "Memes", coins: "top 2" },
  { name: "Layer 2", coins: "top 2" },
  { name: "RWA", coins: "top 2" },
  { name: "Gaming", coins: "top 2" },
];

export function quoteFor(quotes, sym) {
  if (!Array.isArray(quotes)) return null;
  return quotes.find((q) => q && q.symbol === sym) || null;
}
